import { gql } from '@apollo/client';
import { useMutation } from '@apollo/client/react';
import { useState } from 'react';
import { toast } from 'sonner';

import { EditAccountSelectField } from '../../../EditAccountSelectField';
import { EditCheckboxField } from '../../../EditCheckboxField';
import { EditContactSelectField } from '../../../EditContactSelectField';
import { EditDialog } from '../../../EditDialog';
import { EditFormField } from '../../../EditFormField';
import { EditTextareaField } from '../../../EditTextareaField';
import type { ContactLpPlatformData } from '../queries/contactLpPlatformQuery';
import { GET_CONTACT_LP_PLATFORM } from '../queries/contactLpPlatformQuery';

const UPSERT_CONTACT_LP_PLATFORM = gql`
  mutation UpsertContactLPPlatform(
    $contactId: ID!
    $values: ContactLPPlatformUpsertInput!
  ) {
    upsertContactLpPlatform(contactId: $contactId, values: $values) {
      ok
      created
      contactLpPlatform {
        id
        lpPlatform {
          id
          name
        }
        lpPlatformAdvisor {
          id
          name
        }
        lpPlatformParent {
          id
          name
        }
        lpPlatformSuppress
        lpPlatformSuppressEmails
        dateLpPlatformSuppressUnchecked
        reasonForUncheckedLpPlatformSuppress
      }
    }
  }
`;

const LP_PLATFORM_FIELDS: Record<
  string,
  { apiField: string; isBoolean?: boolean; isDate?: boolean }
> = {
  dateLpPlatformSuppressUnchecked: {
    apiField: 'dateLpPlatformSuppressUnchecked',
    isDate: true
  },
  lpPlatformAdvisorId: { apiField: 'lpPlatformAdvisor' },
  lpPlatformId: { apiField: 'lpPlatform' },
  lpPlatformParentId: { apiField: 'lpPlatformParent' },
  lpPlatformSuppress: { apiField: 'lpPlatformSuppress', isBoolean: true },
  lpPlatformSuppressEmails: {
    apiField: 'lpPlatformSuppressEmails',
    isBoolean: true
  },
  reasonForUncheckedLpPlatformSuppress: {
    apiField: 'reasonForUncheckedLpPlatformSuppress'
  }
};

export interface EditContactLpPlatformDialogProps {
  contactId: string;
  initialValue: ContactLpPlatformData;
  isOpen: boolean;
  onChange: (data: ContactLpPlatformData) => void;
  onOpenChange: (open: boolean) => void;
  onSave: () => void;
  value: ContactLpPlatformData;
}

export function EditContactLpPlatformDialog({
  contactId,
  initialValue,
  isOpen,
  onChange,
  onOpenChange,
  onSave,
  value
}: EditContactLpPlatformDialogProps) {
  const [isSaving, setIsSaving] = useState(false);

  const [upsertLpPlatform] = useMutation(UPSERT_CONTACT_LP_PLATFORM, {
    refetchQueries: [
      { query: GET_CONTACT_LP_PLATFORM, variables: { id: contactId } }
    ]
  });

  const changes = getChangedValues(value, initialValue);
  const isDirty = Object.keys(changes).length > 0;

  const updateField = <K extends keyof ContactLpPlatformData>(
    field: K,
    fieldValue: ContactLpPlatformData[K]
  ) => {
    onChange({ ...value, [field]: fieldValue });
  };

  const handleSave = async () => {
    if (!isDirty) {
      onSave();
      return;
    }

    setIsSaving(true);

    try {
      const response = await upsertLpPlatform({
        variables: { contactId, values: changes }
      });

      const data = response.data as {
        upsertContactLpPlatform?: { ok?: boolean };
      };

      if (data.upsertContactLpPlatform?.ok === false) {
        toast.error('Failed to save LP Platform');
      } else {
        toast.success('LP Platform saved successfully');
        onSave();
      }
    } catch (error) {
      console.error('Error saving LP Platform:', error);
      toast.error('Failed to save LP Platform');
    } finally {
      setIsSaving(false);
    }
  };

  return (
    <EditDialog
      isDirty={isDirty}
      isOpen={isOpen}
      isSaving={isSaving}
      onOpenChange={onOpenChange}
      onSave={handleSave}
      title="LP Platform"
    >
      <div className="grid grid-cols-2 gap-x-6 gap-y-4">
        <EditAccountSelectField
          label="LP Platform"
          onChange={(id, name) => {
            onChange({
              ...value,
              lpPlatform: name,
              lpPlatformId: id || null
            });
          }}
          value={
            value.lpPlatformId
              ? { id: value.lpPlatformId, name: value.lpPlatform }
              : null
          }
        />
        <EditContactSelectField
          label="LP Platform Advisor"
          onChange={(id, name) => {
            onChange({
              ...value,
              lpPlatformAdvisor: name,
              lpPlatformAdvisorId: id || null
            });
          }}
          value={
            value.lpPlatformAdvisorId
              ? { id: value.lpPlatformAdvisorId, name: value.lpPlatformAdvisor }
              : null
          }
        />
        <EditAccountSelectField
          label="LP Platform Parent"
          onChange={(id, name) => {
            onChange({
              ...value,
              lpPlatformParent: name,
              lpPlatformParentId: id || null
            });
          }}
          value={
            value.lpPlatformParentId
              ? { id: value.lpPlatformParentId, name: value.lpPlatformParent }
              : null
          }
        />
        <EditCheckboxField
          checked={value.lpPlatformSuppress}
          label="LP Platform Suppress"
          onCheckedChange={v => {
            updateField('lpPlatformSuppress', v);
          }}
        />
        <EditCheckboxField
          checked={value.lpPlatformSuppressEmails}
          label="LP Platform Suppress Emails"
          onCheckedChange={v => {
            updateField('lpPlatformSuppressEmails', v);
          }}
        />
        <EditFormField
          label="Date LP Platform Suppress Unchecked"
          onChange={v => {
            updateField('dateLpPlatformSuppressUnchecked', v);
          }}
          type="date"
          value={toDateInputValue(value.dateLpPlatformSuppressUnchecked) ?? ''}
        />
        <div className="col-span-2">
          <EditTextareaField
            label="Reason for Unchecked LP Platform Suppress"
            onChange={v => {
              updateField('reasonForUncheckedLpPlatformSuppress', v);
            }}
            value={value.reasonForUncheckedLpPlatformSuppress}
          />
        </div>
      </div>
    </EditDialog>
  );
}

function getChangedValues(
  current: ContactLpPlatformData,
  initial: ContactLpPlatformData
): Record<string, boolean | null | string> {
  const changes: Record<string, boolean | null | string> = {};

  for (const [uiField, config] of Object.entries(LP_PLATFORM_FIELDS)) {
    const currentVal = current[uiField as keyof ContactLpPlatformData];
    const initialVal = initial[uiField as keyof ContactLpPlatformData];

    if (currentVal !== initialVal) {
      if (config.isBoolean) {
        changes[config.apiField] = currentVal as boolean;
      } else if (config.isDate) {
        changes[config.apiField] = toDateInputValue(currentVal as string);
      } else {
        const strVal = currentVal as null | string;
        changes[config.apiField] = strVal === '' ? null : (strVal ?? null);
      }
    }
  }

  return changes;
}

function toDateInputValue(dateStr: string): null | string {
  if (!dateStr) return null;
  const date = new Date(dateStr);
  if (isNaN(date.getTime())) return null;
  return date.toISOString().split('T')[0];
}
